import { ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { get } from 'lodash';
import {
  BidItem,
  BidListService,
  BuyErrors,
  ConfigurationState,
  FinanceSource,
  LaunchDarklyService,
  UserService
} from 'remarketing-angular-library';
import { ModalComponent, SpinnerComponent, TrapFocusComponent } from 'remarketing-ui-components-lib';
import { HttpErrorResponse } from '@angular/common/http';
import { of, Subject } from 'rxjs';
import { BidModalItem } from '../../models/bid-modal-item.model';
import { BidResponse } from '../../models/bid-response.model';
import { catchError, takeUntil } from 'rxjs/operators';
import { AnalyticsService, WebsocketService, CurrencyFormatService } from 'smartauction-frontend-micro-library';
import { Store } from '@ngxs/store';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { CommonModule } from '@angular/common';
import { VehicleChangedWarningComponent } from '../offer-modals/vehicle-changed-warning/vehicle-changed-warning.component';
import { ActionSubmittingComponent } from 'src/shared/ui/comment/modal-actions/action-submitting/action-submitting.component';
import { AutoBidButtonTextPipe } from 'src/vehicles/pipes/auto-bid-status/auto-bid-button-text.pipe';
import { FormsModule } from '@angular/forms';
import { ActionSubmittedComponent } from 'src/shared/ui/comment/modal-actions/action-submitted/action-submitted.component';
import { NgxMaskModule } from 'ngx-mask';
import { RouterLink } from '@angular/router';
import { AuctionItemFee } from 'remarketing-angular-library/interfaces/auction-item';
import { DripFeeSource, DripPricingService } from '../../../shared/services/drip-pricing/public-api';
import { FeeToolTipService } from '../../../shared/services/feetooltip/feetooltip.service';
import {
  ModalPriceBreakdownComponent,
  modalTotalDueOf,
  showsTransportationFee
} from '../../../shared/components/modal-price-breakdown/modal-price-breakdown.component';
import {
  ModalPriceBreakdownPattern,
  ModalPriceBreakdownRow,
  ModalPriceBreakdownTranslations
} from '../../../shared/components/modal-price-breakdown/modal-price-breakdown.types';

export const enum BidActionType {
  SINGLE,
  AUTO
}

@Component({
    selector: 'app-bid-modal',
    templateUrl: './bid-modal.component.html',
    styleUrls: ['./bid-modal.component.scss'],
    imports: [
        TranslocoDirective,
        SpinnerComponent,
        ModalComponent,
        CommonModule,
        VehicleChangedWarningComponent,
        ActionSubmittingComponent,
        ActionSubmittedComponent,
        TrapFocusComponent,
        AutoBidButtonTextPipe,
        FormsModule,
        NgxMaskModule,
        RouterLink,
        ModalPriceBreakdownComponent
    ]
})
export class BidModalComponent implements OnInit, OnDestroy {
  @Input() tenantName: string;
  @Input() modalId = 'Bid-Modal';
  @Input() item: BidModalItem;
  @Input() buyerAuctionItemStatus: string;
  @Input() existingAutoBidAmount: number;
  @Input() bidAllowed: boolean;
  @Input() autoBidEnabled: boolean;
  @Input() financeSource: FinanceSource;
  /**
   * The host page's fee payload, and the all-in breakdown's preferred fee source.
   *
   * Bound by the vehicle detail pages, which load it with the vehicle and keep it current. Left unset
   * by the bid list, which launches this modal through ModalLauncherService and has no equivalent; see
   * dripFeeSource for how the two paths resolve.
   */
  @Input() auctionItemFee: AuctionItemFee;
  @Output() bidPlaced = new EventEmitter<BidResponse>();
  @Output() maxAutoBidAmount = new EventEmitter<number>();
  @Output() bidItemFromSuccessfulBid = new EventEmitter<BidItem>();
  @ViewChild(ModalComponent)
  modal: ModalComponent;
  bidAmount: number;
  newBid: number;
  /** The fetched bid list item, kept for its `buyFee` -- the all-in breakdown's fee source. */
  bidItem: BidItem;
  errorMessage: string;
  isSingleBidValue = true;
  SINGLE = BidActionType.SINGLE;
  AUTO = BidActionType.AUTO;
  placeBidName = 'Place Bid';
  placeAutoBidName = 'Place Auto Bid';

  public isLoading = true;
  public showWarning = false;
  bidState: 'notBidding' | 'bidding' | 'biddingComplete' = 'notBidding';
  bidResponse: BidResponse;
  private destroyed$ = new Subject();
  sellingDisclosureUrl: string;

  modalText: any;

  bidModalTranslations = {
    headerPlace: '',
    headerSelect: '',
    ariaLabelDecreaseBid: '',
    ariaLabelAmountDecrease: '',
    viewOnlyPurchaseActionError: '',
    bidModalError: '',
    labelBid: '',
    labelAutoBid: '',
    textImportant: '',
    textBySelecting: '',
    textYouBid: '',
    textBidUpTo: '',
    textOnThe: '',
    textAdditionalFees: '',
    textBindingAgreement: '',
    textTransportationService: ''
  };

  get isCanadianTenantEnabled(): boolean {
    return this.launchDarklyService.getFeatureFlag('canadianTenantEnabled', false);
  }

  get frenchCanadian(): boolean {
    const frenchLanguage = this.translocoService.getActiveLang().includes('fr');
    return frenchLanguage && this.isCanadianTenantEnabled;
  }

  get bidAmountText() {
    return this.bidAmount ? this.formatCurrency(this.bidAmount, false) : this.formatCurrency(0, false);
  }

  get isViewOnly() {
    return this.store.selectSnapshot(ConfigurationState.hasRole('View_Only'));
  }

  get isNotViewOnlyErrorMessage() {
    return this.errorMessage && !this.isViewOnly;
  }

  get displayErrorMessage() {
    if (this.errorMessage) {
      return this.isViewOnly
        ? this.bidModalTranslations.viewOnlyPurchaseActionError
        : this.bidModalTranslations.bidModalError;
    }
  }

  get setAriaLabel() {
    return this.bidAmount <= this.item.currentAmount
      ? this.bidModalTranslations.ariaLabelDecreaseBid
      : this.bidModalTranslations.ariaLabelAmountDecrease;
  }

  get vin() {
    return this.item?.itemDetail?.vin;
  }

  constructor(
    private bidListService: BidListService,
    private _websocket: WebsocketService,
    private readonly analyticsService: AnalyticsService,
    private readonly store: Store,
    private readonly translocoService: TranslocoService,
    private readonly changeDetector: ChangeDetectorRef,
    private readonly currencyFormatService: CurrencyFormatService,
    private readonly launchDarklyService: LaunchDarklyService,
    private readonly userService: UserService,
    private readonly dripPricingService: DripPricingService,
    private readonly feeToolTipService: FeeToolTipService
  ) {}

  ngOnInit() {
    this.store
      .select(state => state.configuration.configuration)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(configuration => {
        this.sellingDisclosureUrl = get(configuration, 'sellingDisclosureUrl', null);
      });
    this.getTranslations();
  }

  getTranslations() {
    const keyParams = {
      'vehicles.bidModal': {},
      error: {}
    };

    this.translocoService
      .selectTranslateObject(keyParams)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(translate => {
        this.bidModalTranslations.ariaLabelDecreaseBid = translate[0].ariaLabelDecreaseBid;
        this.bidModalTranslations.ariaLabelAmountDecrease = translate[0].ariaLabelAmountDecrease;
        this.bidModalTranslations.headerSelect = translate[0].headerSelect;
        this.bidModalTranslations.headerPlace = translate[0].headerPlace;
        this.bidModalTranslations.bidModalError = translate[0].bidModalError;
        this.bidModalTranslations.viewOnlyPurchaseActionError = translate[1].viewOnlyPurchaseActionError;
        this.bidModalTranslations.labelBid = translate[0].labelBid;
        this.bidModalTranslations.labelAutoBid = translate[0].labelAutoBid;
        this.bidModalTranslations.textImportant = translate[0].textImportant;
        this.bidModalTranslations.textBySelecting = translate[0].textBySelecting;
        this.bidModalTranslations.textYouBid = translate[0].textYouBid;
        this.bidModalTranslations.textBidUpTo = translate[0].textBidUpTo;
        this.bidModalTranslations.textOnThe = translate[0].textOnThe;
        this.bidModalTranslations.textAdditionalFees = translate[0].textAdditionalFees;
        this.bidModalTranslations.textBindingAgreement = translate[0].textBindingAgreement;
        this.bidModalTranslations.textTransportationService = translate[0].textTransportationService;
        this.modalText = {
          labelBidAmount: translate[0].labelBidAmount,
          labelMaxAutoBidAmount: translate[0].labelMaxAutoBidAmount,
          labelSubmittingBid: translate[0].labelSubmittingBid,
          labelSubmittingAutoBid: translate[0].labelSubmittingAutoBid,
          labelBidSubmitted: translate[0].labelBidSubmitted,
          labelAutoBidSubmitted: translate[0].labelAutoBidSubmitted
        };
      });
  }

  get showSellingDisclosure(): boolean {
    return this.launchDarklyService.getFeatureFlag('disclosuresLinkEnabled', false);
  }

  getCurrentPrice() {
    return get(this.item, 'currentAmount', null);
  }

  formatCurrency(currency, withCents) {
    return this.currencyFormatService.formatCurrency(currency, withCents).replaceAll('-', '');
  }

  get headerText() {
    if (this.autoBidEnabled) {
      return this.bidModalTranslations.headerSelect;
    } else {
      return this.bidModalTranslations.headerPlace;
    }
  }

  ngOnDestroy() {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  increment(): number {
    return this.bidAmount ? (this.bidAmount += 100) : (this.bidAmount = 100);
  }

  decrement() {
    this.newBid = this.bidAmount - 100;
    if (this.existingAutoBidAmount) {
      if (this.newBid < this.item.currentAmount) {
        return false;
      }
    } else {
      if (
        this.newBid < this.item.currentAmount ||
        (this.newBid === this.item.currentAmount && !this.item.openingPrice)
      ) {
        return false;
      }
    }

    this.bidAmount = this.newBid;
  }

  bid() {
    this.bidState = 'bidding';

    if (this.isViewOnly) {
      this.errorMessage = 'W-REMPLT-AIS-VOU';
      this.bidState = 'notBidding';
      return;
    }

    this.bidAmount = this.parsedBidAmount();
    if (this.bidAmount % 100 !== 0) {
      this.errorMessage = 'W-REMPLT-AIS-MKOHUN';
      this.bidState = 'notBidding';
      return;
    }

    const { auctionItemUuid, buyerOeUuid, financeSourceUuid, financeSourceAch, localDelivery } = this.item;
    const payload = {
      auctionItemUuid,
      buyerOeUuid,
      financeSourceAch,
      financeSourceUuid,
      localDelivery,
      amount: this.bidAmount
    };

    if (this.isSingleBid()) {
      this.bidListService
        .bidAction(payload)
        .pipe(takeUntil(this.destroyed$))
        .subscribe(
          bidResponse => {
            this.bidResponse = bidResponse;
            this.bidState = 'biddingComplete';
            this.bidItemFromSuccessfulBid.emit(this.bidResponse.bidlistItemDto);
            this.analyticsService.track('customEvent', {
              name: this.placeBidName,
              attr: this.analyticsService.createBidEventAnalyticsPayload(payload, this.placeBidName, null)
            });
            this.analyticsService.fireBuyActionEvent('bidBuyAction', 1, [this.item.auctionItemUuid]);
            this.changeDetector.markForCheck();
          },
          error => {
            if (error) {
              this.errorMessage = error.error.errors[0].code;
            }
            this.bidState = 'notBidding';
            this.analyticsService.track(
              'customError',
              error.error.errors[0].title,
              null,
              this.analyticsService.createBidEventAnalyticsPayload(
                payload,
                this.placeBidName,
                error.error.errors[0].title
              )
            );
            this.changeDetector.markForCheck();
          }
        );
    } else {
      this.bidListService
        .autoBidCreate(payload)
        .pipe(takeUntil(this.destroyed$))
        .subscribe(
          bidResponse => {
            this.bidResponse = bidResponse;
            this.bidState = 'biddingComplete';
            this.maxAutoBidAmount.emit(this.bidAmount);
            this.analyticsService.track('customEvent', {
              name: this.placeAutoBidName,
              attr: this.analyticsService.createBidEventAnalyticsPayload(payload, this.placeAutoBidName, null)
            });
            this.analyticsService.fireBuyActionEvent('autoBuyAction', 1, [this.item.auctionItemUuid]);
            this.changeDetector.markForCheck();
          },
          error => {
            if (error) {
              this.errorMessage = error.error.errors[0].code;
            }
            this.bidState = 'notBidding';
            this.analyticsService.track(
              'customError',
              this.errorMessage,
              null,
              this.analyticsService.createBidEventAnalyticsPayload(payload, this.placeAutoBidName, this.errorMessage)
            );
            this.changeDetector.markForCheck();
          }
        );
    }
  }

  show() {
    this.errorMessage = null;
    this.bidState = 'notBidding';
    this.isLoading = true;
    // Cleared with the amount: this modal instance is reused across listings, and stale fees would be
    // added to a different vehicle's bid.
    this.bidItem = undefined;
    // Reset the current amount every time the modal is opened
    this.resetBidAmount();

    if (this.item && this.item.auctionItemUuid) {
      this.bidListService
        .getBidListItem(this.item.auctionItemUuid)
        .pipe(
          takeUntil(this.destroyed$),
          catchError(() => of(undefined))
        )
        .subscribe((bidListItem: BidItem) => {
          // The whole item is kept now, not just the warning flag it used to be mapped to: its
          // `buyFee` is the fee source the all-in breakdown needs, and this response already carries
          // it. The alternative -- a second request, or a fees input threaded through every call site
          // -- would buy nothing. A failed fetch leaves bidItem undefined, which switches the
          // breakdown off rather than showing a bid total with the fees silently missing.
          this.bidItem = bidListItem;
          this.showWarning = !!bidListItem?.buyerConfirmationRequired;
          this.isLoading = false;
          this.changeDetector.markForCheck();
        });
    } else {
      this.isLoading = false;
    }

    this.modal.show();

    setTimeout(() => {
      this.focusHeader();
    }, 2000);
  }

  focusHeader() {
    const modalHeader = document.getElementById('bid-modal-title');

    if (modalHeader) {
      modalHeader.focus();
    }
  }

  close() {
    const isConnected = this._websocket.isConnected;
    if (!isConnected && this.bidState === 'biddingComplete' && this.isSingleBid()) {
      this.bidPlaced.emit(this.bidResponse);
    }

    this.modal.close();
  }

  private getBidError(errorResponse: HttpErrorResponse): string {
    /* istanbul ignore next */
    if (errorResponse.error instanceof ErrorEvent) {
      return BuyErrors.MSG_BUY_002;
    }
    const error = get(errorResponse, 'error.errors[0]', { code: 'No Error Found' });
    return BuyErrors.fromBuyErrors(error.code);
  }

  // returns if action is a single bid or auto bid.
  isSingleBid() {
    return this.isSingleBidValue && !this.existingAutoBidAmount && this.bidAllowed;
  }

  isFinanceSourceFeeIncluded() {
    return this.financeSource?.feeAmount != null && this.financeSource?.feeAmount != 0;
  }

  /**
   * The bid amount as digits.
   *
   * The masked field hands back strings such as '59,000' while the stepper buttons hand back numbers,
   * so this is the single place either becomes a number. bid() submits exactly this value and the
   * breakdown totals exactly this value, which is what makes the displayed total the number the buyer
   * is charged rather than a second opinion about it. Two parsers here would be the defect.
   *
   * String() rather than a null check, matching what bid() did inline before: a null field yields NaN,
   * which bid()'s `% 100` guard already rejects and which the breakdown renders as '$--'.
   */
  private parsedBidAmount(): number {
    return parseInt(String(this.bidAmount).replace(/[^\d]/g, ''), 10);
  }

  // ---------------------------------------------------------------------------
  // All-in pricing (drip pricing, Canada's Competition Act) -- M4.3
  //
  // Pattern B: the buyer supplies the amount, so there is no system-composed headline to decompose.
  // The stepper is projected into the breakdown's base-amount row and the total beneath it updates as
  // the buyer works it, so the all-in figure they are bound to is visible before they commit rather
  // than implied by "plus any applicable charges or fees".
  //
  // M4.11 answers the copy question M4.3 left open. M4.3 kept "plus any applicable charges or fees"
  // on the grounds that a bid genuinely does have its fees added on top of the amount the buyer names,
  // and referred the wording to the story owner. The comps answer it: both Bid frames replace that
  // whole paragraph with a single sentence quoting the breakdown's total, and mark today's copy
  // hidden. So the prose IS reworded here after all -- see dripBidTotalDue for which figure it names.
  // ---------------------------------------------------------------------------

  /** Pattern B: the base amount is the buyer's own, rendered as the projected stepper. */
  readonly additivePattern = ModalPriceBreakdownPattern.ADDITIVE;

  /**
   * The host page's fees where there are any, and the fetched bid list item's otherwise.
   *
   * The two launch paths carry different payloads, so the modal takes whichever its launcher has
   * rather than deciding for itself. A detail page binds auctionItemFee; the bid list opens this
   * through ModalLauncherService and leaves it unset, so that path resolves to bidItem?.buyFee and
   * behaves exactly as it did before this input existed.
   *
   * auctionItemFee is NOT merely a fallback for the bid list fetch -- it takes precedence over it,
   * including for a vehicle that IS on the buyer's list. Two reasons. It is the payload the page
   * around this modal is already pricing from, so preferring it is what stops the modal and the page
   * behind it quoting different all-in totals for one vehicle. And it stays current: the detail page
   * refreshes it from the fee-calculation and websocket updates, while bidItem is a single response
   * fetched once when the modal opened and never revised, so a bid landing mid-session leaves it
   * stale.
   *
   * Reading bidItem?.buyFee alone -- which is what this did until the fee source was widened -- tied
   * the breakdown to bid list membership. A vehicle reached from Buy Search is not on the list, the
   * fetch returns nothing to take a buyFee from, and the modal fell back to the pre-drip design while
   * the page behind it showed all-in prices. Membership is not a drip gate; the flag and the presence
   * of fees are the only two conditions (see showDripBreakdown).
   */
  private get dripFeeSource(): DripFeeSource {
    return this.auctionItemFee?.buyFee ?? this.bidItem?.buyFee;
  }

  /**
   * The breakdown's base amount: what the buyer has entered, or undefined when the field is empty.
   *
   * undefined rather than 0, so an empty field renders the '$--' placeholder. A total of $0 under a
   * binding-bid disclosure is a materially wrong figure; a missing one is merely missing.
   */
  get dripBidBaseAmount(): number | undefined {
    const parsed = this.parsedBidAmount();
    return Number.isFinite(parsed) ? parsed : undefined;
  }

  /**
   * Two conditions only -- the flag and the fee source.
   *
   * Deliberately NOT gated on having a bid amount, unlike the Buy Now modal. There the amount is
   * system-supplied and its absence means something is wrong; here the buyer types it, so an empty
   * amount is the normal opening state and the breakdown has to be on screen to be typed into.
   *
   * A fee payload is required because it IS the fee source: without one requiredFeesOf returns [] and
   * the "total" would equal the bid amount, presenting a bare bid as an all-in figure.
   *
   * Tested with hasFeePayload on the fee source rather than `!!this.bidItem`. `bidItem` is the response
   * the fees arrive IN, not the fees: a bidItem that came back without a buyFee is truthy and carries
   * nothing, so `!!` admits exactly the case the paragraph above says it exists to exclude. The same
   * goes for a buyFee of `{}` or `{ fees: null }`. hasFeePayload asks whether a fee COLLECTION arrived.
   *
   * Not requiredFeesOf(...).length either, and the service docblock says so explicitly: a payload that
   * arrived carrying no required fee must still render, with the all-in total legitimately equal to the
   * bare amount. Gating on the row count would withhold the breakdown from a correct zero-fee answer.
   *
   * And no unusable required fee. requiredFeesOf drops a fee whose amount is not finite, so the rows and
   * the TOTAL PURCHASE PRICE would be short by exactly the fee the buyer must pay -- the failure
   * hasUnusableRequiredFee exists to prevent. Buy Now, Withdraw and Counter catch it through
   * allInPriceOf; this modal has no all-in price of its own to check, because the buyer types the amount.
   */
  get showDripBreakdown(): boolean {
    return this.dripPricingService.isDripPricingEnabled
      && this.dripPricingService.hasFeePayload(this.dripFeeSource)
      && !this.dripPricingService.hasUnusableRequiredFee(this.dripFeeSource);
  }

  get dripTransportationFee(): number | undefined {
    return this.dripPricingService.transportationFeeOf(this.dripFeeSource);
  }

  /**
   * Governs the transportation row's companion paragraph. Same exported helper the row itself uses, so
   * the two cannot drift -- do not re-derive it as `fee > 0`.
   */
  get showTransportationDisclosure(): boolean {
    return showsTransportationFee(this.dripTransportationFee);
  }

  /**
   * The figure the binding paragraph quotes: the modal total, transportation INCLUDED.
   *
   * modalTotalDueOf, not DripPricingService.allInPriceOf. Both Bid comps (Figma 10360:9149 and
   * 10360:9114) quote the TOTAL PURCHASE PRICE their own breakdown renders -- $58,750 on a $58,000 bid
   * with a $400 service fee and $350 of transportation -- and the matched Buy Now pair (10360:9184 with
   * transportation, 10360:9217 without) settles why: the sentence names the total on screen, whatever
   * that total happens to contain. Quoting the all-in price instead would put a number in the prose
   * that appears nowhere in the itemisation directly above it.
   *
   * Same choice M4.10 made for View Offer, and it is what makes the gated transportation sentence
   * load-bearing rather than decorative: an inclusive total is only fair to call binding while the
   * paragraph beneath it says transportation is avoidable.
   */
  get dripBidTotalDue(): number | undefined {
    return modalTotalDueOf(this.dripBidBaseAmount, this.dripFeeRows, this.dripTransportationFee);
  }

  /**
   * The quoted figure as text, falling back to the breakdown's own '$--' placeholder.
   *
   * The placeholder branch is specific to this modal. Every other all-in surface quotes an amount the
   * system already knows, but here the buyer types it, so an empty field is the normal opening state
   * and dripBidTotalDue is legitimately undefined. formatCurrency(undefined) would render a bare '$'
   * inside a binding sentence; borrowing textTotalPlaceholder keeps the prose and the total row saying
   * the same thing about a bid that has not been entered yet.
   *
   * `true` for cents: a binding figure is quoted in full rather than rounded, as Buy Now's is.
   */
  get bidQuotedPriceDisplay(): string {
    return this.dripBidTotalDue === undefined
      ? this.feeToolTipService.dripTranslations.textTotalPlaceholder
      : this.formatCurrency(this.dripBidTotalDue, true);
  }

  /**
   * The fee rows, cached by content.
   *
   * Not an optimisation: translatedRequiredFeesOf maps a fresh array per call, and a template binding
   * is re-evaluated every change-detection pass, so returning it directly would hand the OnPush child
   * a new identity each pass and trip NG0100 in development builds. Keying on the serialised content
   * keeps the reference stable while still picking up a real change.
   */
  get dripFeeRows(): ModalPriceBreakdownRow[] {
    const rows = this.feeToolTipService.translatedRequiredFeesOf(this.dripFeeSource);
    const key = JSON.stringify(rows);
    if (key !== this.dripFeeRowsKey) {
      this.dripFeeRowsKey = key;
      this.dripFeeRowsCache = rows;
    }
    return this.dripFeeRowsCache;
  }

  /**
   * Cached by content for the same reason as dripFeeRows.
   *
   * labelBaseAmount comes from getBidAmountText(), so switching the Bid/Auto Bid radio relabels the
   * row 'ENTER BID AMOUNT' or 'ENTER MAX AUTOBID AMOUNT' -- the content key changes with it, which is
   * why this cache does not stick to the first label it sees.
   */
  get dripBreakdownTranslations(): ModalPriceBreakdownTranslations {
    const drip = this.feeToolTipService.dripTranslations;
    const translations: ModalPriceBreakdownTranslations = {
      labelBaseAmount: this.getBidAmountText(),
      // Unused by ADDITIVE, which has no sub-rows, but the contract is required.
      labelVehiclePrice: drip.labelVehiclePrice,
      labelTransportationFee: this.feeToolTipService.feeTranslations.TRANSPORTATION_FEE,
      // labelModalTotalDue, never labelTotalPurchasePrice: this total INCLUDES transportation while the
      // list-surface total excludes it. See the collision note on FeeToolTipService.dripTranslations.
      labelTotalDue: drip.labelModalTotalDue,
      textTotalPlaceholder: drip.textTotalPlaceholder,
      ariaLabelPriceComponents: drip.ariaLabelPriceComponents
    };
    const key = JSON.stringify(translations);
    if (key !== this.dripBreakdownTranslationsKey) {
      this.dripBreakdownTranslationsKey = key;
      this.dripBreakdownTranslationsCache = translations;
    }
    return this.dripBreakdownTranslationsCache;
  }

  private dripFeeRowsKey: string;
  private dripFeeRowsCache: ModalPriceBreakdownRow[];
  private dripBreakdownTranslationsKey: string;
  private dripBreakdownTranslationsCache: ModalPriceBreakdownTranslations;

  resetBidAmount() {
    if (this.item) {
      if (this.existingAutoBidAmount) {
        return (this.bidAmount = this.existingAutoBidAmount);
      } else {
        return this.item.openingPrice
          ? (this.bidAmount = this.item.currentAmount)
          : (this.bidAmount = this.item.currentAmount + 100);
      }
    }
  }

  updateBidType(updatedBidType: BidActionType) {
    if (!this.bidAmount) {
      this.resetBidAmount();
    }
    this.isSingleBidValue = BidActionType.SINGLE === updatedBidType;
  }
  getBidAmountText() {
    return this.isSingleBid() ? this.modalText.labelBidAmount : this.modalText.labelMaxAutoBidAmount;
  }

  getActionText() {
    return this.isSingleBid() ? this.modalText.labelSubmittingBid : this.modalText.labelSubmittingAutoBid;
  }

  getSuccessText() {
    return this.isSingleBid() ? this.modalText.labelBidSubmitted : this.modalText.labelAutoBidSubmitted;
  }

  getBidButtonAnalyticsTag() {
    if (this.existingAutoBidAmount) {
      return 'updateAutoBuyAction';
    }
    return this.isSingleBid() ? 'bidBuyAction' : 'autoBuyAction';
  }
}
